import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Building2, User, FileText, Shield } from "lucide-react";

// Honeypot field for bot detection (hidden from users)
const HONEYPOT_FIELD = "website_url";

interface ConsultationFormProps {
  lightTheme?: boolean;
}

const ConsultationForm = ({ lightTheme = false }: ConsultationFormProps) => {
  // Personal Information
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [position, setPosition] = useState("");
  
  // Company Information
  const [company, setCompany] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [taxId, setTaxId] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [website, setWebsite] = useState("");
  
  // Business Details
  const [industry, setIndustry] = useState("");
  const [numberOfEmployees, setNumberOfEmployees] = useState("");
  const [yearsInBusiness, setYearsInBusiness] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  
  const [serviceType, setServiceType] = useState("");
  const [message, setMessage] = useState("");
  
  // Compliance Acknowledgments
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptDataProcessing, setAcceptDataProcessing] = useState(false);
  const [confirmBusinessLegitimacy, setConfirmBusinessLegitimacy] = useState(false);
  const [acceptBackgroundCheck, setAcceptBackgroundCheck] = useState(false);
  
  // Honeypot field for bot detection
  const [honeypot, setHoneypot] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  // Theme-based classes
  const textPrimary = lightTheme ? "text-gray-900" : "text-white";
  const textSecondary = lightTheme ? "text-gray-600" : "text-gray-300";
  const textMuted = lightTheme ? "text-gray-500" : "text-gray-400";
  const borderColor = lightTheme ? "border-gray-200" : "border-white/20";
  const inputBg = lightTheme ? "bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400" : "bg-white/5 border-white/20 text-white placeholder:text-gray-400";
  const selectTriggerBg = lightTheme ? "bg-gray-50 border-gray-300 text-gray-900" : "bg-white/5 border-white/20 text-white";
  const complianceBg = lightTheme ? "bg-gray-50 border-gray-200" : "bg-white/5 border-white/20";
  const checkboxStyle = lightTheme 
    ? "border-gray-400 data-[state=checked]:bg-gray-900 data-[state=checked]:text-white mt-1" 
    : "border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-black mt-1";
  const buttonStyle = lightTheme 
    ? "bg-gray-900 text-white hover:bg-gray-800 font-semibold py-6 text-lg" 
    : "bg-white text-black hover:bg-gray-200 font-semibold py-6 text-lg";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !company || !serviceType || !registrationNumber) {
      toast({
        variant: "destructive",
        title: "Missing information",
        description: "Please fill in all required fields",
      });
      return;
    }

    if (!acceptTerms || !acceptDataProcessing || !confirmBusinessLegitimacy || !acceptBackgroundCheck) {
      toast({
        variant: "destructive",
        title: "Compliance Required",
        description: "Please accept all compliance acknowledgments to proceed",
      });
      return;
    }

    setLoading(true);

    try {
      // Use edge function for server-side validation
      const { data, error } = await supabase.functions.invoke('submit-consultation', {
        body: {
          name,
          email,
          phone: phone || null,
          position: position || null,
          company,
          registrationNumber: registrationNumber || null,
          taxId: taxId || null,
          businessAddress: businessAddress || null,
          website: website || null,
          industry: industry || null,
          numberOfEmployees: numberOfEmployees || null,
          yearsInBusiness: yearsInBusiness || null,
          businessDescription: businessDescription || null,
          serviceType,
          message: message || null,
          // Honeypot field - should be empty for real users
          [HONEYPOT_FIELD]: honeypot,
        },
      });

      if (error) {
        throw new Error(error.message || 'Submission failed');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setSubmitted(true);
      toast({
        title: "Registration request submitted!",
        description: "We'll review your application within 24-48 hours",
      });

      // Reset form
      setTimeout(() => {
        setName("");
        setEmail("");
        setPhone("");
        setPosition("");
        setCompany("");
        setRegistrationNumber("");
        setTaxId("");
        setBusinessAddress("");
        setWebsite("");
        setIndustry("");
        setNumberOfEmployees("");
        setYearsInBusiness("");
        setBusinessDescription("");
        setServiceType("");
        setMessage("");
        setAcceptTerms(false);
        setAcceptDataProcessing(false);
        setConfirmBusinessLegitimacy(false);
        setAcceptBackgroundCheck(false);
        setHoneypot("");
        setSubmitted(false);
      }, 3000);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Submission failed",
        description: error.message || "An error occurred. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="py-8 text-center">
        <CheckCircle className={`w-16 h-16 ${lightTheme ? "text-green-600" : "text-green-400"} mx-auto mb-4`} />
        <h3 className={`text-2xl font-bold ${textPrimary} mb-2`}>Thank You!</h3>
        <p className={textSecondary}>
          Your registration request has been submitted successfully.
        </p>
        <p className={`${textMuted} text-sm mt-2`}>
          Our Platform Control team will review your application and contact you within 24-48 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Personal Information Section */}
      <div className="space-y-4">
        <h3 className={`text-lg font-semibold ${textPrimary} border-b ${borderColor} pb-2 flex items-center gap-2`}>
          <User className="w-5 h-5" />
          Personal Contact Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name" className={textPrimary}>Full Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={inputBg}
              placeholder="John Doe"
            />
          </div>
          <div>
            <Label htmlFor="email" className={textPrimary}>Business Email *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={inputBg}
              placeholder="john@company.com"
            />
          </div>
          <div>
            <Label htmlFor="phone" className={textPrimary}>Phone Number *</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              className={inputBg}
              placeholder="+27 123 456 789"
            />
          </div>
          <div>
            <Label htmlFor="position" className={textPrimary}>Position/Title *</Label>
            <Select value={position} onValueChange={setPosition} required>
              <SelectTrigger className={selectTriggerBg}>
                <SelectValue placeholder="Select your position" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ceo">CEO / Founder</SelectItem>
                <SelectItem value="coo">COO / Operations Director</SelectItem>
                <SelectItem value="cfo">CFO / Finance Director</SelectItem>
                <SelectItem value="manager">General Manager</SelectItem>
                <SelectItem value="director">Director</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
                <SelectItem value="hr">HR Manager</SelectItem>
                <SelectItem value="other">Other Executive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Company Information Section */}
      <div className="space-y-4">
        <h3 className={`text-lg font-semibold ${textPrimary} border-b ${borderColor} pb-2 flex items-center gap-2`}>
          <Building2 className="w-5 h-5" />
          Company Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="company" className={textPrimary}>Company / Agency Name *</Label>
            <Input
              id="company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              required
              className={inputBg}
              placeholder="Your Agency Ltd"
            />
          </div>
          <div>
            <Label htmlFor="registrationNumber" className={textPrimary}>Business Registration Number *</Label>
            <Input
              id="registrationNumber"
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.target.value)}
              required
              className={inputBg}
              placeholder="2024/123456/07"
            />
          </div>
          <div>
            <Label htmlFor="taxId" className={textPrimary}>Tax ID / VAT Number</Label>
            <Input
              id="taxId"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              className={inputBg}
              placeholder="9876543210"
            />
          </div>
          <div>
            <Label htmlFor="website" className={textPrimary}>Company Website</Label>
            <Input
              id="website"
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className={inputBg}
              placeholder="https://yourcompany.com"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="businessAddress" className={textPrimary}>Business Address *</Label>
          <Textarea
            id="businessAddress"
            value={businessAddress}
            onChange={(e) => setBusinessAddress(e.target.value)}
            required
            rows={2}
            className={inputBg}
            placeholder="123 Business Street, City, Province, Postal Code"
          />
        </div>
      </div>

      {/* Business Details Section */}
      <div className="space-y-4">
        <h3 className={`text-lg font-semibold ${textPrimary} border-b ${borderColor} pb-2 flex items-center gap-2`}>
          <FileText className="w-5 h-5" />
          Business Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="industry" className={textPrimary}>Industry *</Label>
            <Select value={industry} onValueChange={setIndustry} required>
              <SelectTrigger className={selectTriggerBg}>
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="entertainment">Entertainment & Events</SelectItem>
                <SelectItem value="hospitality">Hospitality</SelectItem>
                <SelectItem value="music">Music & Recording</SelectItem>
                <SelectItem value="talent">Talent Management</SelectItem>
                <SelectItem value="marketing">Marketing & Advertising</SelectItem>
                <SelectItem value="media">Media & Broadcasting</SelectItem>
                <SelectItem value="technology">Technology</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="numberOfEmployees" className={textPrimary}>Number of Employees *</Label>
            <Select value={numberOfEmployees} onValueChange={setNumberOfEmployees} required>
              <SelectTrigger className={selectTriggerBg}>
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1-10">1-10</SelectItem>
                <SelectItem value="11-50">11-50</SelectItem>
                <SelectItem value="51-200">51-200</SelectItem>
                <SelectItem value="201-500">201-500</SelectItem>
                <SelectItem value="500+">500+</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="yearsInBusiness" className={textPrimary}>Years in Business *</Label>
            <Input
              id="yearsInBusiness"
              type="number"
              min="0"
              value={yearsInBusiness}
              onChange={(e) => setYearsInBusiness(e.target.value)}
              required
              className={inputBg}
              placeholder="5"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="businessDescription" className={textPrimary}>Business Description *</Label>
          <Textarea
            id="businessDescription"
            value={businessDescription}
            onChange={(e) => setBusinessDescription(e.target.value)}
            required
            rows={3}
            className={inputBg}
            placeholder="Describe your agency's operations, the type of creators/venues you manage, and your experience in the entertainment industry..."
          />
        </div>
      </div>

      {/* Service Selection Section */}
      <div className="space-y-4">
        <h3 className={`text-lg font-semibold ${textPrimary} border-b ${borderColor} pb-2`}>
          Dashboard Access Request
        </h3>
        <div>
          <Label htmlFor="service" className={textPrimary}>Dashboard Access Requested *</Label>
          <Select value={serviceType} onValueChange={setServiceType} required>
            <SelectTrigger className={selectTriggerBg}>
              <SelectValue placeholder="Select dashboard type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="creator_management">Creator Management Dashboard (R3,499/month)</SelectItem>
              <SelectItem value="venue_oversight">Venue Oversight Dashboard (R5,299/month)</SelectItem>
              <SelectItem value="both">Both Dashboards (Custom Pricing)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="message" className={textPrimary}>Why do you want to join MCG? *</Label>
          <Textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={4}
            className={inputBg}
            placeholder="Tell us about your goals, the number of creators/venues you plan to manage, and how you envision using the MCG platform..."
          />
        </div>
      </div>

      {/* Compliance Acknowledgments */}
      <div className="space-y-4">
        <h3 className={`text-lg font-semibold ${textPrimary} border-b ${borderColor} pb-2 flex items-center gap-2`}>
          <Shield className="w-5 h-5" />
          Compliance Acknowledgments
        </h3>
        <div className={`${complianceBg} border rounded-lg p-4 space-y-4`}>
          <div className="flex items-start space-x-3">
            <Checkbox 
              id="terms" 
              checked={acceptTerms}
              onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
              className={checkboxStyle}
            />
            <Label htmlFor="terms" className={`${textSecondary} text-sm cursor-pointer`}>
              I have read and agree to the Metro City Groove{" "}
              <Link to="/terms" className="text-blue-600 hover:underline" target="_blank">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/agency-partnership-agreement" className="text-blue-600 hover:underline" target="_blank">Agency Partnership Agreement</Link>
              . I understand the responsibilities and obligations as an agency partner.
            </Label>
          </div>
          
          <div className="flex items-start space-x-3">
            <Checkbox 
              id="dataProcessing" 
              checked={acceptDataProcessing}
              onCheckedChange={(checked) => setAcceptDataProcessing(checked as boolean)}
              className={checkboxStyle}
            />
            <Label htmlFor="dataProcessing" className={`${textSecondary} text-sm cursor-pointer`}>
              I consent to the processing of personal and business data in accordance with{" "}
              <Link to="/privacy" className="text-blue-600 hover:underline" target="_blank">POPIA (Protection of Personal Information Act)</Link>
              {" "}and understand how my data will be used.
            </Label>
          </div>
          
          <div className="flex items-start space-x-3">
            <Checkbox 
              id="businessLegitimacy" 
              checked={confirmBusinessLegitimacy}
              onCheckedChange={(checked) => setConfirmBusinessLegitimacy(checked as boolean)}
              className={checkboxStyle}
            />
            <Label htmlFor="businessLegitimacy" className={`${textSecondary} text-sm cursor-pointer`}>
              I confirm that all information provided is accurate and that my business is legally registered and in good standing. I understand that providing false information may result in account termination.
            </Label>
          </div>
          
          <div className="flex items-start space-x-3">
            <Checkbox 
              id="backgroundCheck" 
              checked={acceptBackgroundCheck}
              onCheckedChange={(checked) => setAcceptBackgroundCheck(checked as boolean)}
              className={checkboxStyle}
            />
            <Label htmlFor="backgroundCheck" className={`${textSecondary} text-sm cursor-pointer`}>
              I authorize Metro City Groove to conduct{" "}
              <Link to="/business-verification-policy" className="text-blue-600 hover:underline" target="_blank">verification checks</Link>
              {" "}on my business, including but not limited to: CIPC registration verification, tax compliance checks, and reference verification.
            </Label>
          </div>
        </div>
      </div>

      <Button 
        type="submit" 
        className={`w-full ${buttonStyle}`}
        disabled={loading || !acceptTerms || !acceptDataProcessing || !confirmBusinessLegitimacy || !acceptBackgroundCheck}
      >
        {loading ? "Submitting Registration Request..." : "Submit Registration Request"}
      </Button>
      
      <p className={`text-sm ${textMuted} text-center`}>
        * Required fields. Your application will be reviewed by our Platform Control team within 24-48 hours.
        Upon approval, you will receive login credentials and access to the compliance checklist.
      </p>
    </form>
  );
};

export default ConsultationForm;
